import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import { ListChecks, FloppyDisk, ArrowClockwise, Plus, Trash, ArrowUp, ArrowDown, GlobeHemisphereWest, Folder } from "@phosphor-icons/react";

const TASK_TYPES = [
  { key: "task", label: "Task", color: "#0A0A0A" },
  { key: "story", label: "Story", color: "#059669" },
  { key: "bug", label: "Bug", color: "#DC2626" },
  { key: "epic", label: "Epic", color: "#7C3AED" },
];

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown (single)" },
  { value: "multiselect", label: "Dropdown (multi)" },
  { value: "checkbox", label: "Checkbox" },
  { value: "url", label: "URL" },
  { value: "user", label: "User picker" },
];

const emptyField = () => ({
  key: "",
  label: "",
  type: "text",
  required: false,
  options: [],
  default: null,
  help: "",
  appears_on_card: false,
});

export default function TaskFormsTab({ scopedProjectId = null }) {
  const { confirm } = useDialog();
  // `scopedProjectId` (when supplied by the project-toolbar shortcut) forces
  // the tab into project-only editing mode. Otherwise the user can toggle
  // between "Global (all projects)" and any project they can access.
  const [activeType, setActiveType] = useState("task");
  const [scope, setScope] = useState(scopedProjectId || "*"); // "*" = global, or project_id
  const [projects, setProjects] = useState([]);
  const [template, setTemplate] = useState(null);
  const [globalPreview, setGlobalPreview] = useState([]); // read-only preview when editing a project scope
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState(emptyField());

  const flash = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: "", text: "" }), 3500); };

  // Load project list (only when the user is choosing scope themselves)
  useEffect(() => {
    if (scopedProjectId) return;
    api.get("/projects").then(r => setProjects(r.data || [])).catch(() => {});
  }, [scopedProjectId]);

  const load = async (type, scopeVal) => {
    setTemplate(null);
    try {
      const params = scopeVal && scopeVal !== "*" ? { project_id: scopeVal } : {};
      const r = await api.get(`/settings/task-forms/${type}`, { params });
      setTemplate(r.data);
      // If editing a project scope, also fetch the read-only global preview
      if (scopeVal && scopeVal !== "*") {
        try {
          const g = await api.get(`/settings/task-forms/${type}`);
          setGlobalPreview(g.data.fields || []);
        } catch { setGlobalPreview([]); }
      } else {
        setGlobalPreview([]);
      }
    } catch (e) { flash("error", formatAxiosError(e)); }
  };

  useEffect(() => { load(activeType, scope); }, [activeType, scope]);

  const save = async () => {
    if (!template) return;
    setBusy(true);
    try {
      const params = scope && scope !== "*" ? { project_id: scope } : {};
      const r = await api.put(`/settings/task-forms/${activeType}`, { fields: template.fields }, { params });
      setTemplate(r.data);
      flash("ok", `${activeType.toUpperCase()} form saved for ${scope === "*" ? "all projects" : "this project"}.`);
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    const target = scope === "*" ? "global default" : "empty (project-specific)";
    const ok = await confirm({
      title: `Reset the "${activeType}" form?`,
      description: `The form will be reset to ${target}. Custom fields at this scope will be lost.`,
      confirmText: "Reset form",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const params = scope && scope !== "*" ? { project_id: scope } : {};
      const r = await api.post(`/settings/task-forms/${activeType}/reset`, null, { params });
      setTemplate(r.data);
      flash("ok", `${activeType.toUpperCase()} form reset.`);
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  const updateField = (idx, patch) => {
    setTemplate(t => ({ ...t, fields: t.fields.map((f, i) => i === idx ? { ...f, ...patch } : f) }));
  };
  const removeField = (idx) => {
    setTemplate(t => ({ ...t, fields: t.fields.filter((_, i) => i !== idx) }));
  };
  const moveField = (idx, dir) => {
    setTemplate(t => {
      const arr = [...t.fields];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return t;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...t, fields: arr };
    });
  };

  const addField = () => {
    const key = (draft.key || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (!key || !/^[a-z0-9_]+$/.test(key)) { flash("error", "Key must be snake_case (a-z 0-9 _)"); return; }
    if (!draft.label.trim()) { flash("error", "Label is required"); return; }
    if (template.fields.some(f => f.key === key)) { flash("error", "That key already exists"); return; }
    setTemplate(t => ({ ...t, fields: [...t.fields, { ...draft, key }] }));
    setDraft(emptyField());
    setShowAdd(false);
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex items-center gap-2">
        <ListChecks size={20} weight="bold" className="text-[#002FA7]" />
        <h2 className="font-display text-2xl font-black">Task forms</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Configure custom fields on the create / edit form for each work-item type.
        <b> Global</b> fields apply to every project. Switch to a specific project to add
        <b> project-only</b> fields that show up in addition to the global ones (additive).
      </p>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`}>{msg.text}</div>
      )}

      {/* Scope selector */}
      {!scopedProjectId && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <div className="eyebrow text-[10px]">Scope</div>
          <button
            onClick={() => setScope("*")}
            className={`px-3 py-1.5 text-xs border flex items-center gap-1 ${scope === "*" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-border hover:bg-muted"}`}
            data-testid="scope-global"
          >
            <GlobeHemisphereWest size={12} weight="bold" /> Global (all projects)
          </button>
          <select
            value={scope !== "*" ? scope : ""}
            onChange={(e) => setScope(e.target.value || "*")}
            className="border border-border px-3 py-1.5 text-xs bg-white"
            data-testid="scope-project"
          >
            <option value="">— Or choose a specific project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      )}

      {/* Type pill selector */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {TASK_TYPES.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveType(t.key)}
            className={`px-4 py-2 text-sm font-semibold border ${activeType === t.key ? "text-white" : "bg-white hover:bg-muted"}`}
            style={activeType === t.key ? { backgroundColor: t.color, borderColor: t.color } : { borderColor: "#E5E7EB", color: t.color }}
            data-testid={`taskform-type-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Global preview (read-only) shown above the project-scoped editor */}
      {scope !== "*" && globalPreview.length > 0 && (
        <div className="mb-4 border border-dashed border-border bg-muted/30">
          <div className="px-4 py-2 text-xs uppercase font-mono-mini text-muted-foreground flex items-center gap-2">
            <GlobeHemisphereWest size={12} weight="bold" /> Global fields (from all projects) — read-only here
          </div>
          <div className="px-4 pb-3 flex flex-wrap gap-1.5">
            {globalPreview.map(f => (
              <span key={f.key} className="text-[11px] font-mono-mini border border-border bg-white px-2 py-0.5">
                {f.label} <span className="text-muted-foreground">· {f.type}</span>{f.required && " *"}
              </span>
            ))}
          </div>
        </div>
      )}

      {!template && <div className="text-sm text-muted-foreground">Loading…</div>}

      {template && (
        <div className="border border-border">
          <div className="px-4 py-3 border-b border-border bg-muted/50 flex items-center justify-between">
            <div className="font-mono-mini text-xs uppercase">{template.fields.length} custom fields</div>
            <div className="flex gap-2">
              <button onClick={() => setShowAdd(v => !v)} className="border border-border px-3 py-1.5 text-xs hover:bg-white flex items-center gap-1" data-testid="taskform-add-field">
                <Plus size={12} weight="bold" /> Add field
              </button>
              <button onClick={reset} disabled={busy} className="border border-border px-3 py-1.5 text-xs hover:bg-white flex items-center gap-1" data-testid="taskform-reset">
                <ArrowClockwise size={12} weight="bold" /> Reset
              </button>
              <button onClick={save} disabled={busy} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1" data-testid="taskform-save">
                <FloppyDisk size={12} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          {showAdd && (
            <div className="p-4 border-b border-border bg-white space-y-3">
              <div className="text-xs uppercase font-mono-mini text-muted-foreground">New field</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="eyebrow block mb-1 text-[10px]">Label *</label>
                  <input value={draft.label} onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} placeholder="e.g. Severity" className="input" data-testid="taskform-draft-label" />
                </div>
                <div>
                  <label className="eyebrow block mb-1 text-[10px]">Key (snake_case)</label>
                  <input value={draft.key} onChange={e => setDraft(d => ({ ...d, key: e.target.value }))} placeholder="e.g. severity" className="input" data-testid="taskform-draft-key" />
                </div>
                <div>
                  <label className="eyebrow block mb-1 text-[10px]">Type</label>
                  <select value={draft.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value }))} className="input" data-testid="taskform-draft-type">
                    {FIELD_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="eyebrow block mb-1 text-[10px]">Help text (optional)</label>
                  <input value={draft.help} onChange={e => setDraft(d => ({ ...d, help: e.target.value }))} className="input" />
                </div>
                {(draft.type === "select" || draft.type === "multiselect") && (
                  <div className="md:col-span-3">
                    <label className="eyebrow block mb-1 text-[10px]">Options (one per line)</label>
                    <textarea rows={3} value={(draft.options || []).join("\n")} onChange={e => setDraft(d => ({ ...d, options: e.target.value.split("\n").map(x => x.trim()).filter(Boolean) }))} className="input font-mono-mini" />
                  </div>
                )}
                <div className="flex items-center gap-4 md:col-span-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.required} onChange={e => setDraft(d => ({ ...d, required: e.target.checked }))} />
                    Required
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.appears_on_card} onChange={e => setDraft(d => ({ ...d, appears_on_card: e.target.checked }))} />
                    Show on card
                  </label>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => { setShowAdd(false); setDraft(emptyField()); }} className="border border-border px-3 py-1.5 text-xs">Cancel</button>
                <button onClick={addField} className="btn-primary text-xs px-3 py-1.5" data-testid="taskform-draft-save">Add</button>
              </div>
            </div>
          )}

          {template.fields.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No custom fields yet. Click "Add field" to get started.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase font-mono-mini">
                <tr>
                  <th className="text-left px-3 py-2 w-16"></th>
                  <th className="text-left px-3 py-2">Label / Key</th>
                  <th className="text-left px-3 py-2 w-40">Type</th>
                  <th className="text-center px-3 py-2 w-24">Required</th>
                  <th className="text-center px-3 py-2 w-24">On card</th>
                  <th className="text-right px-3 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {template.fields.map((f, idx) => (
                  <tr key={f.key} className="border-t border-border hover:bg-muted/20" data-testid={`taskform-field-${f.key}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-white disabled:opacity-30"><ArrowUp size={12} /></button>
                        <button onClick={() => moveField(idx, 1)} disabled={idx === template.fields.length - 1} className="p-1 hover:bg-white disabled:opacity-30"><ArrowDown size={12} /></button>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <input value={f.label} onChange={e => updateField(idx, { label: e.target.value })} className="w-full font-semibold bg-transparent focus:outline-none border-b border-transparent focus:border-[#002FA7]" />
                      <div className="text-[10px] font-mono-mini text-muted-foreground">{f.key}</div>
                      {(f.type === "select" || f.type === "multiselect") && (
                        <input placeholder="Options (comma-separated)" value={(f.options || []).join(", ")} onChange={e => updateField(idx, { options: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} className="w-full text-xs mt-1 border border-border px-2 py-1" />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select value={f.type} onChange={e => updateField(idx, { type: e.target.value })} className="w-full border border-border px-2 py-1 text-xs bg-white">
                        {FIELD_TYPES.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={!!f.required} onChange={e => updateField(idx, { required: e.target.checked })} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={!!f.appears_on_card} onChange={e => updateField(idx, { appears_on_card: e.target.checked })} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => removeField(idx)} className="text-muted-foreground hover:text-[#FF3B30]" data-testid={`taskform-delete-${f.key}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
